import { DivIcon, LatLngTuple } from 'leaflet';
import { Marker, Popup } from 'react-leaflet';
import { Workorder } from '@certhon/models';
import { useEffect, useMemo, useRef } from 'react';

interface WorkorderMapPinProps {
  workorder: Workorder;
  position: LatLngTuple;
  color: string;
  highlighted?: boolean;
  onHover?: (entering: boolean) => void;
  onClick?: () => void;
}

function createColoredIcon(
  workorderId: number,
  color: string,
  highlighted: boolean,
): DivIcon {
  const scale = highlighted ? 1.2 : 1;
  const size = 28 * scale;
  const anchor = size / 2;

  return new DivIcon({
    className: 'workorder-marker',
    html: `
      <div style="
        display: flex;
        align-items: center;
        justify-content: center;
        width: ${size}px;
        height: ${size}px;
        background: ${color};
        color: #fff;
        border-radius: 50%;
        font-size: ${10 * scale}px;
        font-weight: 600;
        box-shadow: 0 2px 4px rgba(0,0,0,0.3);
        border: 2px solid #fff;
        transition: all 0.2s ease;
        cursor: pointer;
      ">
        W${workorderId}
      </div>
    `,
    iconSize: [size, size],
    iconAnchor: [anchor, anchor],
  });
}

export function WorkorderMapPin({
  workorder,
  position,
  color,
  highlighted = false,
  onHover,
  onClick,
}: WorkorderMapPinProps) {
  const markerRef = useRef<L.Marker>(null);

  const icon = useMemo(
    () => createColoredIcon(workorder.id, color, highlighted),
    [workorder.id, color, highlighted],
  );

  // Handle hover events
  useEffect(() => {
    const marker = markerRef.current;
    if (!marker || !onHover) return;

    const handleMouseEnter = () => onHover(true);
    const handleMouseLeave = () => onHover(false);

    marker.on('mouseover', handleMouseEnter);
    marker.on('mouseout', handleMouseLeave);

    return () => {
      marker.off('mouseover', handleMouseEnter);
      marker.off('mouseout', handleMouseLeave);
    };
  }, [onHover]);

  // Handle click events
  useEffect(() => {
    const marker = markerRef.current;
    if (!marker || !onClick) return;

    const handleClick = () => onClick();

    marker.on('click', handleClick);

    return () => {
      marker.off('click', handleClick);
    };
  }, [onClick]);

  return (
    <Marker ref={markerRef} position={position} icon={icon}>
      <Popup>
        <div className="min-w-[200px]">
          <strong className="text-sm">
            W{workorder.id}
            {workorder.customer_name && ` - ${workorder.customer_name}`}
          </strong>
          {workorder.location_address && (
            <>
              <br />
              <span className="text-muted-foreground text-xs">
                {workorder.location_address}
              </span>
            </>
          )}
          {workorder.execution_date && (
            <>
              <br />
              <span className="text-muted-foreground text-xs">
                {new Date(workorder.execution_date).toLocaleDateString()}
              </span>
            </>
          )}
          {workorder.progress && (
            <>
              <br />
              <span className="text-xs capitalize">
                Status: {workorder.progress.replace('_', ' ')}
              </span>
            </>
          )}
        </div>
      </Popup>
    </Marker>
  );
}
